import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import Swal from 'sweetalert2';
import { ArrowLeft, Save, UploadCloud, X, Clock, Calendar, ChevronLeft, ChevronRight, Info, Plus, Trash2 } from 'lucide-react';
import { API_URL } from '../../config';

// Common styling for input/select fields
const fieldStyle = {
  width: '100%',
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  padding: '1.2rem 1.5rem',
  borderRadius: '1.25rem',
  color: 'white',
  fontSize: '0.95rem',
  outline: 'none',
  transition: 'all 0.3s ease',
  boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5)'
};

const labelStyle = {
  display: 'flex',
  color: '#94a3b8',
  marginBottom: '0.75rem',
  fontSize: '0.8rem',
  fontWeight: '700',
  letterSpacing: '0.05em',
  textTransform: 'uppercase'
};

const hrStyle = {
  color: 'white',
  fontSize: '1.2rem',
  fontWeight: '800',
  borderBottom: '2px solid rgba(0, 209, 255, 0.1)',
  paddingBottom: '1rem',
  marginBottom: '1rem',
  marginTop: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem'
};

const optionStyle = {
  background: '#0f172a',
  color: '#fff',
  padding: '10px'
};

const FormSelect = ({ label, name, value, onChange, options, required, hideSpacer }) => (
  <div>
    {label && <label style={labelStyle}>{label}</label>}
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <div style={{ position: 'relative', flex: 1 }}>
        <select 
          name={name} 
          value={value} 
          onChange={onChange} 
          required={required}
          style={{...fieldStyle, appearance: 'none', paddingRight: '3rem'}}
        >
          {options.map((opt, i) => (
            <option key={i} value={opt.value} style={optionStyle}>{opt.label}</option>
          ))}
        </select>
        <div style={{ 
          position: 'absolute', right: '1.2rem', top: '50%', transform: 'translateY(-50%)', 
          pointerEvents: 'none', color: '#00d1ff', fontSize: '0.7rem', opacity: 0.7 
        }}>
          ▼
        </div>
      </div>
      {/* Spacer to align with fields that have a button */}
      {!hideSpacer && <div style={{ width: '54px', minWidth: '54px' }}></div>}
    </div>
  </div>
);

const FormNumberInput = ({ label, name, value, onChange, placeholder, hideSpacer }) => {
  // Görüntüleme için değeri formatla (1250000 -> 1.250.000)
  // Görüntüleme için değeri formatla (Düzgünce sayıya çevirip formatlar)
  const displayValue = value !== '' && value !== null ? Number(value).toLocaleString('tr-TR') : '';

  const handleTextChange = (e) => {
    // Sadece rakamları al
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    
    // Parent'a ham rakamı gönder
    const syntheticEvent = {
      target: {
        name: name,
        value: rawValue
      }
    };
    onChange(syntheticEvent);
  };

  return (
    <div>
      {label && <label style={labelStyle}>{label}</label>}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }}>
          <input 
            type="text" 
            inputMode="numeric"
            name={name} 
            value={displayValue} 
            onChange={handleTextChange} 
            placeholder={placeholder}
            style={fieldStyle}
          />
        </div>
        {!hideSpacer && <div style={{ width: '54px', minWidth: '54px' }}></div>}
      </div>
    </div>
  );
};

const LicensePlateInput = ({ name, value, onChange }) => {
  const handleChange = (e) => {
    const val = e.target.value.toUpperCase();
    onChange({ target: { name, value: val } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <label style={labelStyle}>PLAKA SİSTEMİ</label>
      <div style={{
        background: 'white',
        border: '3px solid #1a1a1a',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        padding: '2px',
        width: 'fit-content',
        minWidth: '320px',
        height: '85px',
        overflow: 'hidden',
        boxShadow: '0 15px 35px rgba(0,0,0,0.4)',
        position: 'relative'
      }}>
        {/* Blue TR Strip */}
        <div style={{
          background: '#003399',
          width: '50px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingBottom: '12px',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '18px',
          flexShrink: 0,
          borderRadius: '8px 0 0 8px'
        }}>
          <span style={{ fontSize: '10px', opacity: 0.8, marginBottom: '2px' }}>★ ★</span>
          TR
        </div>
        
        {/* Plate Content */}
        <input
          type="text"
          value={value || ''}
          onChange={handleChange}
          placeholder="34 AVEC 01"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            textAlign: 'center',
            fontSize: '44px',
            fontWeight: '900',
            color: '#1a1a1a',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            width: '100%',
            background: 'transparent',
            padding: '0 20px',
            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
          }}
        />
        {/* Secret Badge for Admin Only View */}
        <div style={{
          position: 'absolute',
          top: '5px',
          right: '10px',
          fontSize: '8px',
          color: '#94a3b8',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Sadece Admin
        </div>
      </div>
    </div>
  );
};

const AddVehicle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(!!id);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [allBrandsData, setAllBrandsData] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    series: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    type: 'Satılık',
    category: 'Luxury',
    fuel_type: 'Benzin',
    transmission: 'Otomatik',
    vehicle_condition: 'İkinci El',
    mileage: '',
    body_type: 'Sedan',
    engine_power: '',
    engine_capacity: '',
    traction: 'Önden Çekiş',
    color: '',
    warranty: 'Yok',
    heavy_damage: 'Yok',
    plate_nationality: 'Türkiye (TR)',
    from_who: 'Galeriden',
    exchange: 'Takasa Uygun',
    description: '',
    damage_amount: 0,
    expertise_note: '',
    seller_name: '',
    seller_phone: '',
    selected_consultant_id: '',
    license_plate: '',
    chassis_no: '',
    purchase_cost: '',
    purchased_from: '',
    purchase_date: '',
    extra_expenses: '',
    operational_status: '',
    internal_notes: '',
    price_daily: '',
    price_weekly: '',
    price_monthly: '',
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [availabilityHistory, setAvailabilityHistory] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  
  // Calendar states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newReservation, setNewReservation] = useState({
    start_date: '',
    end_date: '',
    type: 'Kiralama',
    notes: ''
  });
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [activeDayLabel, setActiveDayLabel] = useState('');
  const [selectedDateStr, setSelectedDateStr] = useState('');
  const [expenseItems, setExpenseItems] = useState([{ description: '', amount: '' }]);
  const typeOptions = ["Kiralama", "Rezerve", "Bakım", "Serviste", "Temizlikte", "Kapora Alındı"];

  // Ekspertiz: Araç parça durumları (Matching Reference Image Colors)
  const EXPERTISE_STATUSES = [
    { key: 'original', label: 'Orijinal', color: '#9ca3af' },
    { key: 'local_painted', label: 'Lokal Boyalı', color: '#f97316' }, // Orange for LB
    { key: 'painted', label: 'Boyalı', color: '#3b82f6' }, // Blue for B
    { key: 'changed', label: 'Değişen', color: '#ef4444' }, // Red for D
  ];

  const CAR_PARTS = [
    { key: 'hood', label: 'Kaput' },
    { key: 'roof', label: 'Tavan' },
    { key: 'trunk', label: 'Bagaj' },
    { key: 'front_bumper', label: 'Ön Tampon' },
    { key: 'rear_bumper', label: 'Arka Tampon' },
    { key: 'front_left_fender', label: 'Sol Ön Çamurluk' },
    { key: 'front_right_fender', label: 'Sağ Ön Çamurluk' },
    { key: 'rear_left_fender', label: 'Sol Arka Çamurluk' },
    { key: 'rear_right_fender', label: 'Sağ Arka Çamurluk' },
    { key: 'front_left_door', label: 'Sol Ön Kapı' },
    { key: 'front_right_door', label: 'Sağ Ön Kapı' },
    { key: 'rear_left_door', label: 'Sol Arka Kapı' },
    { key: 'rear_right_door', label: 'Sağ Arka Kapı' },
  ];

  // User-requested specific default expertise report
  const defaultExpertise = {
    hood: 'original',
    roof: 'original',
    trunk: 'original',
    front_bumper: 'original',
    rear_bumper: 'original',
    front_left_fender: 'original',
    front_right_fender: 'original',
    rear_left_fender: 'original',
    rear_right_fender: 'original',
    front_left_door: 'original',
    front_right_door: 'original',
    rear_left_door: 'original',
    rear_right_door: 'original',
  };

  const [expertiseReport, setExpertiseReport] = useState(defaultExpertise);

  // Donanım özellikleri
  const FEATURE_GROUPS = {
    'Güvenlik': ['ABS', 'ESP / VSA', 'BAS', 'Hava Yastığı (Sürücü)', 'Hava Yastığı (Yolcu)', 'İsofix', 'Merkezi Kilit', 'İmmobilizer', 'Şerit Takip Sistemi', 'Gece Görüş Sistemi', 'Kör Nokta Uyarı Sistemi', 'Yorgunluk Tespit Sistemi', 'Yokuş Kalkış Desteği', 'Çocuk Kilidi', 'AEB', 'Distronic'],
    'İç Donanım': ['Deri Koltuk', 'Kumaş Koltuk', 'Elektrikli Camlar', 'Klima', 'Otomatik Klima', 'Start / Stop', 'Yol Bilgisayarı', 'Ön Koltuk Kol Dayaması', 'Head-up Display', 'Isıtmalı Direksiyon', 'Koltuklar (Isıtmalı)', 'Koltuklar (Soğutmalı)', 'Koltuklar (Hafızalı)', 'Elektrikli Koltuklar', 'Hız Sabitleme Sistemi', 'Adaptive Cruise Control', 'Geri Görüş Kamerası', 'Ön Görüş Kamerası', 'Anahtarsız Giriş ve Çalıştırma', 'Hidrolik Direksiyon', 'Fonksiyonel Direksiyon', 'Otm. Kararan Dikiz Aynası', 'Üçüncü Sıra Koltuklar'],
    'Dış Donanım': ['Sunroof', 'Panoramik Cam Tavan', 'Hardtop', 'Far (Adaptif)', 'Park Sensörü (Ön)', 'Park Sensörü (Arka)', 'Park Asistanı', 'Aynalar (Isıtmalı)', 'Aynalar (Hafızalı)', 'Aynalar (Elektrikli)', 'Akıllı Bagaj Kapağı', 'Römork Çeki Demiri', 'Ayakla Açılan Bagaj Kapağı'],
    'Multimedya': ['Android Auto', 'Apple CarPlay', 'Bluetooth', 'USB / AUX', 'Navigasyon', 'CD / DVD', 'Dokunmatik Ekran', 'Kablosuz Şarj'],
  };

  const [selectedFeatures, setSelectedFeatures] = useState([]);

  useEffect(() => {
    fetchBrands();
    fetchAdminUsers();
    if (id) {
      fetchVehicle(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const fetchAdminUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data);
      }
    } catch (err) {
      console.error('Kullanıcılar alınamadı:', err);
    }
  };

  useEffect(() => {
    if (formData.brand) {
      updateModelsList(formData.brand);
    } else {
      setModels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.brand]);

  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/brands`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const formatted = data.map(b => ({
          brand: b.name,
          models: b.models.map(m => m.name)
        }));
        setAllBrandsData(formatted);
        setBrands(formatted);
      }
    } catch (err) {
      console.error('Brands fetching error:', err);
    }
  };

  const updateModelsList = (brandName) => {
    const brandObj = allBrandsData.find(b => b.brand === brandName);
    if (brandObj) {
      setModels(brandObj.models || []);
    } else {
      setModels([]);
    }
  };

  const fetchVehicle = async (vehicleId) => {
    try {
      const response = await fetch(`${API_URL}/admin/vehicles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const vehicles = await response.json();
        const vehicle = vehicles.find(v => v.id === parseInt(vehicleId));
        if (vehicle) {
          setFormData({
            name: vehicle.name || '',
            brand: vehicle.brand || '',
            series: vehicle.series || '',
            model: vehicle.model || '',
            year: vehicle.year || new Date().getFullYear(),
            price: vehicle.price || '',
            type: vehicle.type || 'Satılık',
            category: vehicle.category || 'Luxury',
            fuel_type: vehicle.fuel_type || 'Benzin',
            transmission: vehicle.transmission || 'Otomatik',
            vehicle_condition: vehicle.vehicle_condition || 'İkinci El',
            mileage: vehicle.mileage || '',
            body_type: vehicle.body_type || 'Sedan',
            engine_power: vehicle.engine_power || '',
            engine_capacity: vehicle.engine_capacity || '',
            traction: vehicle.traction || 'Önden Çekiş',
            color: vehicle.color || '',
            warranty: vehicle.warranty || 'Yok',
            heavy_damage: vehicle.heavy_damage || 'Yok',
            plate_nationality: vehicle.plate_nationality || 'Türkiye (TR)',
            from_who: vehicle.from_who || 'Galeriden',
            exchange: vehicle.exchange || 'Takasa Uygun',
            description: vehicle.description || '',
            damage_amount: vehicle.damage_amount || 0,
            expertise_note: vehicle.expertise_note || '',
            seller_name: vehicle.seller_name || '',
            seller_phone: vehicle.seller_phone || '',
            license_plate: vehicle.license_plate || '',
            chassis_no: vehicle.chassis_no || '',
            purchase_cost: vehicle.purchase_cost || '',
            purchased_from: vehicle.purchased_from || '',
            purchase_date: vehicle.purchase_date || '',
            extra_expenses: vehicle.extra_expenses || '',
            operational_status: vehicle.operational_status || '',
            internal_notes: vehicle.internal_notes || '',
            price_daily: vehicle.price_daily || '',
            price_weekly: vehicle.price_weekly || '',
            price_monthly: vehicle.price_monthly || '',
          });
          if (vehicle.expense_breakdown && Array.isArray(vehicle.expense_breakdown)) {
            setExpenseItems(vehicle.expense_breakdown.length > 0 ? vehicle.expense_breakdown : [{ description: '', amount: '' }]);
          }
          setSelectedFiles(vehicle.images || []);
          // Load expertise & features
          if (vehicle.expertise_report && typeof vehicle.expertise_report === 'object') {
            setExpertiseReport(prev => ({...prev, ...vehicle.expertise_report}));
          }
          if (vehicle.features && Array.isArray(vehicle.features)) {
            setSelectedFeatures(vehicle.features);
          }

          // Fetch Availability History
          const availResponse = await fetch(`${API_URL}/admin/availability/${vehicleId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (availResponse.ok) {
            const availData = await availResponse.json();
            setAvailabilityHistory(availData);
          }
        }
      }
    } catch (err) {
      console.error('Araç bilgileri alınamadı:', err);
    } finally {
      setLoading(false);
    }
  };


  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDayMouseDown = (dateStr) => {
    setSelectionStart(dateStr);
    setSelectionEnd(dateStr);
    setIsSelecting(true);
  };

  const handleDayMouseEnter = (dateStr) => {
    if (isSelecting) {
      setSelectionEnd(dateStr);
    }
  };

  const handleDayMouseUp = () => {
    if (isSelecting && selectionStart && selectionEnd) {
      if (selectionStart !== selectionEnd) {
        const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
        const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
        setNewReservation({
          ...newReservation,
          start_date: `${start}T09:00`,
          end_date: `${end}T18:00`
        });
        setEditingId(null);
        setIsModalOpen(true);
      }
    }
    setIsSelecting(false);
  };

  const handleDayClick = (dateStr, events) => {
    if (selectionStart !== selectionEnd) return;
    if (events.length > 0) {
      setSelectedDayEvents(events);
      setActiveDayLabel(new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }));
      setSelectedDateStr(dateStr);
      setIsDayModalOpen(true);
    } else {
      setEditingId(null);
      setNewReservation({
        start_date: `${dateStr}T09:00`,
        end_date: `${dateStr}T18:00`,
        type: 'Kiralama',
        notes: ''
      });
      setIsModalOpen(true);
    }
  };

  const isInSelection = (dateStr) => {
    if (!selectionStart || !selectionEnd) return false;
    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
    return dateStr >= start && dateStr <= end;
  };

  const handleAddReservation = async (e) => {
    if (e) e.preventDefault();
    try {
      const url = editingId 
        ? `${API_URL}/admin/availability/${editingId}`
        : `${API_URL}/admin/availability`;
      const method = editingId ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...newReservation, vehicle_id: id })
      });
      if (response.ok) {
        Swal.fire({ icon: 'success', title: 'Başarılı!', text: 'Takvim güncellendi.', timer: 1500, background: '#0f172a', color: '#fff' });
        setIsModalOpen(false);
        setEditingId(null);
        setNewReservation({ start_date: '', end_date: '', type: 'Kiralama', notes: '' });
        fetchVehicle(id); 
      }
    } catch (err) {
      console.error('Rezervasyon hatası:', err);
    }
  };

  const handleDeleteReservation = async (resId) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      const response = await fetch(`${API_URL}/admin/availability/${resId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchVehicle(id);
      }
    } catch (err) {
      console.error('Silme hatası:', err);
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const calendarDays = [];
    const startingDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    for (let i = 0; i < startingDay; i++) {
        calendarDays.push(<div key={`empty-${i}`} style={{ height: '110px', background: 'rgba(255,255,255,0.01)', borderRight: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        const dayEvents = availabilityHistory.filter(h => {
          const start = h.start_date.split('T')[0];
          const end = h.end_date.split('T')[0];
          return dateStr >= start && dateStr <= end;
        });

        const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
        const isSelected = isInSelection(dateStr);

        calendarDays.push(
            <div key={day} 
                onMouseDown={() => handleDayMouseDown(dateStr)}
                onMouseEnter={() => handleDayMouseEnter(dateStr)}
                onMouseUp={handleDayMouseUp}
                onClick={() => handleDayClick(dateStr, dayEvents)}
                style={{ 
                    height: '110px', 
                    background: isSelected ? 'rgba(0, 209, 255, 0.15)' : isToday ? 'rgba(0, 209, 255, 0.05)' : 'rgba(255,255,255,0.02)', 
                    borderRight: '1px solid rgba(255,255,255,0.05)', 
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                    padding: '0.4rem',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    cursor: 'cell',
                    userSelect: 'none',
                    zIndex: isSelected ? 2 : 1
                }}
            >
                <span style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: isToday || isSelected ? '900' : '600', 
                  color: isToday ? 'var(--primary)' : isSelected ? '#fff' : 'rgba(255,255,255,0.3)',
                  marginBottom: '2px'
                }}>{day}</span>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {dayEvents.map((e, idx) => (
                    <div key={idx} style={{ 
                      fontSize: '0.62rem', 
                      padding: '2px 4px', 
                      borderRadius: '3px',
                      background: (e.type === 'Bakım' || e.type === 'Serviste' || e.type === 'Temizlikte') ? 'rgba(239, 68, 68, 0.2)' : 
                                 (e.type === 'Rezerve' || e.type === 'Kapora Alındı') ? 'rgba(245, 158, 11, 0.2)' : 
                                 'rgba(34, 197, 94, 0.2)',
                      color: (e.type === 'Bakım' || e.type === 'Serviste' || e.type === 'Temizlikte') ? '#f87171' : 
                             (e.type === 'Rezerve' || e.type === 'Kapora Alındı') ? '#fbbf24' : 
                             '#4ade80',
                      borderLeft: `2px solid ${
                        (e.type === 'Bakım' || e.type === 'Serviste' || e.type === 'Temizlikte') ? '#ef4444' : 
                        (e.type === 'Rezerve' || e.type === 'Kapora Alındı') ? '#f59e0b' : 
                        '#22c55e'}`,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginBottom: '2px'
                    }}>
                      {e.type}
                    </div>
                  ))}
                </div>
            </div>
        );
    }
    return calendarDays;
  };


  const handleConsultantChange = (e) => {
    const userId = e.target.value;
    if (userId === 'manual' || userId === '') {
      setFormData(prev => ({ ...prev, selected_consultant_id: userId }));
      return;
    }
    const user = adminUsers.find(u => u.id === parseInt(userId));
    if (user) {
      setFormData(prev => ({
        ...prev,
        selected_consultant_id: userId,
        seller_name: user.full_name || user.username,
        seller_phone: user.phone || ''
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'brand') {
      // Clear model and series when brand changes
      setFormData(prev => ({ ...prev, brand: value, model: '', series: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedFiles(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (index) => {
    if (draggedItemIndex === null) return;
    
    const newFiles = [...selectedFiles];
    const draggedItem = newFiles[draggedItemIndex];
    
    // Remove the dragged item and insert it at the new position
    newFiles.splice(draggedItemIndex, 1);
    newFiles.splice(index, 0, draggedItem);
    
    setSelectedFiles(newFiles);
    setDraggedItemIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Process new files if needed and push to base64, but we already have them in selectedFiles in handleFileChange!
    // So selectedFiles ALREADY contains the existing URLs + new Base64 strings.
    const payload = { 
      ...formData, 
      images: selectedFiles, 
      expertise_report: expertiseReport, 
      features: selectedFeatures,
      expense_breakdown: expenseItems.filter(item => item.description.trim() !== '' || item.amount !== '')
    };

    const url = id 
      ? `${API_URL}/admin/vehicles/${id}`
      : `${API_URL}/admin/vehicles`;
    
    const method = id ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: id ? 'Güncellendi!' : 'Eklendi!',
          text: 'Araç bilgileri başarıyla kaydedildi.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00d1ff',
          timer: 2000
        }).then(() => {
          navigate('/admin/vehicles');
        });
      } else {
        const errData = await response.json();
        Swal.fire({ icon: 'error', title: 'Hata!', text: errData.message || 'İşlem başarısız.', background: '#0f172a', color: '#fff' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Hata!', text: 'Sunucu bağlantı hatası.', background: '#0f172a', color: '#fff' });
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-full min-h-[500px]">
      <div className="text-white font-bold opacity-50 flex items-center gap-3">
        <div className="w-6 h-6 border-2 border-[#00d1ff] border-t-transparent rounded-full animate-spin"></div>
        Yükleniyor...
      </div>
    </div>
  );


  // Ekspertiz: Parça durumunu bir sonraki duruma çevir
  const cycleExpertiseStatus = (partKey) => {
    const currentStatus = expertiseReport[partKey];
    const statusKeys = EXPERTISE_STATUSES.map(s => s.key);
    const currentIndex = statusKeys.indexOf(currentStatus);
    const nextIndex = (currentIndex + 1) % statusKeys.length;
    setExpertiseReport(prev => ({...prev, [partKey]: statusKeys[nextIndex]}));
  };


  const toggleFeature = (feature) => {
    setSelectedFeatures(prev => 
      prev.includes(feature) ? prev.filter(f => f !== feature) : [...prev, feature]
    );
  };


  return (
    <div className="admin-vehicles" style={{ paddingBottom: '4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2.5rem' }}>
        <button 
          onClick={() => navigate('/admin/vehicles')}
          style={{ 
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', 
            width: '50px', height: '50px', borderRadius: '1.2rem', display: 'flex', alignItems: 'center', 
            justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s', 
            boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
          }}
          onMouseOver={(e) => { e.currentTarget.style.background = '#00d1ff'; e.currentTarget.style.color = '#000'; }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'white'; }}
        >
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>
        <div>
          <h1 className="admin-card-title" style={{ fontSize: '2.2rem', marginBottom: '0.3rem', fontWeight: '900', letterSpacing: '-0.5px' }}>
            {id ? 'Aracı Düzenle' : 'Yeni Araç Ekle'}
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
            Aracın tüm ince detaylarını buradan {id ? 'güncelleyebilirsiniz' : 'belirleyebilirsiniz'}. Lütfen verileri eksiksiz girin.
          </p>
        </div>
      </div>

      <div style={{ 
        background: 'rgba(10, 10, 12, 0.9)', 
        borderRadius: '2rem', 
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
        padding: '3rem'
      }}>
        <form onSubmit={handleSubmit} style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '2rem 1.5rem' 
        }}>
          
          {/* Genel Bilgiler */}
          <div style={{ gridColumn: '1 / -1' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#00d1ff', borderRadius: '4px' }}></div>
              GENEL BİLGİLER
            </h3>
          </div>
          
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Araç İlan Başlığı</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required style={fieldStyle} placeholder="Örn: Hatasız Boyasız Fırsat Aracı" />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          {/* Marka */}
          <FormSelect 
            label="Marka" 
            name="brand" 
            value={formData.brand} 
            onChange={handleInputChange} 
            required={true}
            options={[
              { value: '', label: 'Marka Seçiniz' },
              ...brands.map(b => ({ value: b.brand, label: b.brand }))
            ]} 
          />

          {/* Model */}
          <div>
            <label style={labelStyle}>Seri/Model Tipi</label>
            {models.length > 0 ? (
              <FormSelect 
                label="" 
                name="model" 
                value={formData.model} 
                onChange={handleInputChange} 
                required={true}
                options={[
                  { value: '', label: 'Model Seçiniz' },
                  ...models.map(m => ({ value: m, label: m }))
                ]} 
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <input 
                    type="text" 
                    name="model" 
                    value={formData.model} 
                    onChange={handleInputChange} 
                    placeholder={formData.brand ? "Model manuel giriniz" : "Önce marka seçiniz"} 
                    style={fieldStyle} 
                  />
                </div>
                <div style={{ width: '54px', minWidth: '54px' }}></div>
              </div>
            )}
          </div>

          {/* Versiyon */}
          <div>
            <label style={labelStyle}>Versiyon / Paket (Opsiyonel)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <input type="text" name="series" value={formData.series} onChange={handleInputChange} style={fieldStyle} placeholder="Örn: 1.6 TDI Comfortline" />
              </div>
              {/* Spacer to keep alignment with rows that have buttons */}
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          {/* Yıl */}
          <div>
            <label style={labelStyle}>Yıl</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <FormNumberInput 
                  label="" 
                  name="year" 
                  value={formData.year} 
                  onChange={handleInputChange} 
                  placeholder="2024"
                />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          {/* Fiyat - Satılık veya Kiralık'a göre değişir */}
          {formData.type !== 'Kiralık' ? (
            <div>
              <label style={labelStyle}>Satış Fiyatı (₺)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <FormNumberInput 
                    label="" 
                    name="price" 
                    value={formData.price} 
                    onChange={handleInputChange} 
                    placeholder="Örn: 1250000"
                  />
                </div>
                <div style={{ width: '54px', minWidth: '54px' }}></div>
              </div>
            </div>
          ) : (
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ width: '6px', height: '22px', background: 'linear-gradient(to bottom, #00d1ff, #0891b2)', borderRadius: '3px' }}></div>
                <label style={{ ...labelStyle, marginBottom: 0, color: '#00d1ff', fontSize: '0.85rem' }}>
                  🚗 KİRALIK ARAÇ FİYATLANDIRMASI
                </label>
              </div>
              <div style={{ 
                background: 'rgba(0, 209, 255, 0.04)', 
                border: '1px solid rgba(0, 209, 255, 0.15)', 
                borderRadius: '1.5rem', 
                padding: '2rem',
                marginBottom: '0.5rem'
              }}>
                <p style={{ color: '#64748b', fontSize: '0.78rem', marginBottom: '1.5rem', fontWeight: 600 }}>
                  Kiralık araç için günlük, haftalık ve aylık fiyatlardan en az birini girin. Boş bırakılan periyotlar sitede gösterilmez.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
                  
                  {/* Günlük */}
                  <div style={{ 
                    background: 'rgba(34, 197, 94, 0.05)', 
                    border: '1px solid rgba(34, 197, 94, 0.2)', 
                    borderRadius: '1.25rem', 
                    padding: '1.5rem',
                    display: 'flex', flexDirection: 'column', gap: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{ 
                        width: '32px', height: '32px', borderRadius: '10px',
                        background: 'rgba(34, 197, 94, 0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1rem'
                      }}>☀️</div>
                      <div>
                        <div style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Günlük</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 600 }}>Daily Rate</div>
                      </div>
                    </div>
                    <FormNumberInput
                      label=""
                      name="price_daily"
                      value={formData.price_daily}
                      onChange={handleInputChange}
                      placeholder="₺ / gün"
                      hideSpacer={true}
                    />
                  </div>

                  {/* Haftalık */}
                  <div style={{ 
                    background: 'rgba(251, 191, 36, 0.05)', 
                    border: '1px solid rgba(251, 191, 36, 0.2)', 
                    borderRadius: '1.25rem', 
                    padding: '1.5rem',
                    display: 'flex', flexDirection: 'column', gap: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{ 
                        width: '32px', height: '32px', borderRadius: '10px',
                        background: 'rgba(251, 191, 36, 0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1rem'
                      }}>📅</div>
                      <div>
                        <div style={{ color: '#fbbf24', fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Haftalık</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 600 }}>Weekly Rate</div>
                      </div>
                    </div>
                    <FormNumberInput
                      label=""
                      name="price_weekly"
                      value={formData.price_weekly}
                      onChange={handleInputChange}
                      placeholder="₺ / hafta"
                      hideSpacer={true}
                    />
                  </div>

                  {/* Aylık */}
                  <div style={{ 
                    background: 'rgba(167, 139, 250, 0.05)', 
                    border: '1px solid rgba(167, 139, 250, 0.2)', 
                    borderRadius: '1.25rem', 
                    padding: '1.5rem',
                    display: 'flex', flexDirection: 'column', gap: '1rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{ 
                        width: '32px', height: '32px', borderRadius: '10px',
                        background: 'rgba(167, 139, 250, 0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1rem'
                      }}>🗓️</div>
                      <div>
                        <div style={{ color: '#a78bfa', fontSize: '0.7rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Aylık</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.65rem', fontWeight: 600 }}>Monthly Rate</div>
                      </div>
                    </div>
                    <FormNumberInput
                      label=""
                      name="price_monthly"
                      value={formData.price_monthly}
                      onChange={handleInputChange}
                      placeholder="₺ / ay"
                      hideSpacer={true}
                    />
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* Kilometre */}
          <div>
            <label style={labelStyle}>Kilometre (KM)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <FormNumberInput 
                  label="" 
                  name="mileage" 
                  value={formData.mileage} 
                  onChange={handleInputChange} 
                  placeholder="Örn: 125000"
                />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          {/* Teknik Detaylar */}
          <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#eab308', borderRadius: '4px' }}></div>
              TEKNİK DETAYLAR
            </h3>
          </div>

          <FormSelect 
            label="Yakıt Tipi" 
            name="fuel_type" 
            value={formData.fuel_type} 
            onChange={handleInputChange} 
            options={[
              { value: 'Benzin', label: 'Benzin' },
              { value: 'Dizel', label: 'Dizel' },
              { value: 'Elektrik', label: 'Elektrik' },
              { value: 'Hibrit', label: 'Hibrit' },
              { value: 'LPG', label: 'LPG' }
            ]} 
          />

          <FormSelect 
            label="Vites" 
            name="transmission" 
            value={formData.transmission} 
            onChange={handleInputChange} 
            options={[
              { value: 'Otomatik', label: 'Otomatik' },
              { value: 'Manuel', label: 'Manuel' },
              { value: 'Yarı Otomatik', label: 'Yarı Otomatik' }
            ]} 
          />

          <FormSelect 
            label="Kasa Tipi" 
            name="body_type" 
            value={formData.body_type} 
            onChange={handleInputChange} 
            options={[
              { value: 'Sedan', label: 'Sedan' },
              { value: 'Hatchback', label: 'Hatchback' },
              { value: 'SUV', label: 'SUV' },
              { value: 'Station Wagon', label: 'Station Wagon' },
              { value: 'Coupe', label: 'Coupe' },
              { value: 'Cabrio', label: 'Cabrio' }
            ]} 
          />

          <div>
            <label style={labelStyle}>Motor Gücü (HP)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <input type="text" name="engine_power" value={formData.engine_power} onChange={handleInputChange} style={fieldStyle} placeholder="110 HP" />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Motor Hacmi (cc)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <input type="text" name="engine_capacity" value={formData.engine_capacity} onChange={handleInputChange} style={fieldStyle} placeholder="1598 cc" />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          <FormSelect 
            label="Çekiş" 
            name="traction" 
            value={formData.traction} 
            onChange={handleInputChange} 
            options={[
              { value: 'Önden Çekiş', label: 'Önden Çekiş' },
              { value: 'Arkadan İtiş', label: 'Arkadan İtiş' },
              { value: 'AWD', label: 'AWD' },
              { value: '4WD', label: '4WD (Sürekli)' }
            ]} 
          />

          <div>
            <label style={labelStyle}>Renk</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <input type="text" name="color" value={formData.color} onChange={handleInputChange} style={fieldStyle} placeholder="Beyaz" />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>

          {/* Durum ve Kategori */}
          <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#ec4899', borderRadius: '4px' }}></div>
              DURUM VE KATEGORİ
            </h3>
          </div>

          <FormSelect 
            label="İlan Türü" 
            name="type" 
            value={formData.type} 
            onChange={handleInputChange} 
            options={[
              { value: 'Satılık', label: 'Satılık' },
              { value: 'Kiralık', label: 'Kiralık' }
            ]} 
          />

          <FormSelect 
            label="Kategori (Kısa Etiket)" 
            name="category" 
            value={formData.category} 
            onChange={handleInputChange} 
            options={[
              { value: '', label: 'Kategori Seçiniz...' },
              { value: 'Premium', label: 'Premium' },
              { value: 'Luxury', label: 'Luxury' },
              { value: 'Sport', label: 'Sport' },
              { value: 'Ekonomik', label: 'Ekonomik' },
              { value: 'Aile Aracı', label: 'Aile Aracı' },
              { value: 'Off-Road', label: 'Off-Road' },
              { value: 'Fırsat', label: 'Fırsat' },
              { value: 'Hatasız', label: 'Hatasız' },
              { value: 'Düşük KM', label: 'Düşük KM' },
              { value: 'Full + Full', label: 'Full + Full' },
              { value: 'Garantili', label: 'Garantili' },
              { value: 'Acil', label: 'Acil' },
              { value: 'Koleksiyonluk', label: 'Koleksiyonluk' },
              { value: 'İlk Sahibinden', label: 'İlk Sahibinden' },
              { value: 'Yetkili Servis Bakımlı', label: 'Yetkili Servis Bakımlı' }
            ]} 
          />

          <FormSelect 
            label="Araç Durumu" 
            name="vehicle_condition" 
            value={formData.vehicle_condition} 
            onChange={handleInputChange} 
            options={[
              { value: 'Sıfır', label: 'Sıfır' },
              { value: 'İkinci El', label: 'İkinci El' }
            ]} 
          />

          <FormSelect 
            label="Kimden" 
            name="from_who" 
            value={formData.from_who} 
            onChange={handleInputChange} 
            options={[
              { value: 'Galeriden', label: 'Galeriden' },
              { value: 'Sahibinden', label: 'Sahibinden' }
            ]} 
          />

          <FormSelect 
            label="Ağır Hasar Kayıtlı Mı?" 
            name="heavy_damage" 
            value={formData.heavy_damage} 
            onChange={handleInputChange} 
            options={[
              { value: 'Yok', label: 'Hayır, Yok' },
              { value: 'Ağır Hasarlı', label: 'Evet, Ağır Hasarlı' }
            ]} 
          />

          <FormSelect 
            label="Plaka / Uyruk" 
            name="plate_nationality" 
            value={formData.plate_nationality} 
            onChange={handleInputChange} 
            options={[
              { value: 'Türkiye (TR)', label: 'Türkiye (TR)' },
              { value: 'Yabancı Plaka', label: 'Yabancı Plaka' }
            ]} 
          />

          <FormSelect 
            label="Takasa Uygunluk" 
            name="exchange" 
            value={formData.exchange} 
            onChange={handleInputChange} 
            options={[
              { value: 'Takasa Uygun', label: 'Takasa Uygun' },
              { value: 'Uygun Değil', label: 'Uygun Değil' }
            ]} 
          />

          <FormSelect 
            label="Garanti Durumu" 
            name="warranty" 
            value={formData.warranty} 
            onChange={handleInputChange} 
            options={[
              { value: 'Yok', label: 'Yok' },
              { value: 'Garantisi Var', label: 'Garantisi Var' }
            ]} 
          />

          {/* EKSPERTİZ RAPORU */}
          <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#8b5cf6', borderRadius: '4px' }}></div>
              EKSPERTİZ RAPORU
            </h3>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            {/* Side-by-Side Expertise Layout */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', alignItems: 'flex-start', marginTop: '2rem' }}>
              {/* Left Column: Legend + Car Diagram */}
              <div style={{ width: 'fit-content', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Legend - Detailed & Minimalist */}
                <div style={{ display: 'flex', gap: '0.8rem', marginBottom: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  {EXPERTISE_STATUSES.map(s => (
                    <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{ width: '14px', height: '14px', background: s.color, borderRadius: '4px' }}></div>
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '900', textTransform: 'uppercase' }}>{s.label}</span>
                    </div>
                  ))}
                </div>

                {/* SVG Diagram - Butterfly Ultra-Naif */}
                 <div style={{ width: '280px', cursor: 'pointer' }}>
                  <svg viewBox="0 0 500 650" style={{ width: '100%', height: 'auto' }}>
                    <g transform="translate(250, 10)">
                      {/* Center Column */}
                      {[
                        { key: 'front_bumper', d: "M -50,15 L 50,15 L 50,30 Q 50,35 45,35 L -45,35 Q -50,35 -50,30 Z", lx: 0, ly: 25 },
                        { key: 'hood', d: "M -55,45 Q 0,35 55,45 L 70,165 Q 0,180 -70,165 Z", lx: 0, ly: 110 },
                        { key: 'roof', d: "M -85,235 Q 0,250 85,235 L 90,420 Q 0,440 -90,420 Z", lx: 0, ly: 330 },
                        { key: 'trunk', d: "M -80,480 Q 0,500 80,480 L 70,590 Q 0,610 -70,590 Z", lx: 0, ly: 540 },
                        { key: 'rear_bumper', d: "M -55,600 L 55,600 L 55,620 Q 55,630 45,630 L -45,630 Q -55,630 -55,620 Z", lx: 0, ly: 615 },
                      ].map(part => {
                        const status = expertiseReport[part.key];
                        const colorMap = { original: '#e5e7eb', local_painted: '#f97316', painted: '#3b82f6', changed: '#ef4444' };
                        const marker = status === 'changed' ? 'D' : status === 'painted' ? 'B' : status === 'local_painted' ? 'LB' : '';
                        return (
                          <g key={part.key} onClick={() => cycleExpertiseStatus(part.key)}>
                            <path d={part.d} fill={colorMap[status]} stroke="rgba(0,0,0,0.1)" strokeWidth="0.4" />
                            {marker && <text x={part.lx} y={part.ly} fill="white" fontSize="24" fontWeight="1000" textAnchor="middle" style={{ pointerEvents: 'none' }}>{marker}</text>}
                          </g>
                        );
                      })}
                      {/* Placeholder Windows */}
                      <path d="M -75,175 Q 0,195 75,175 L 85,225 Q 0,245 -85,225 Z" fill="#94a3b8" opacity="0.3" />
                      <path d="M -90,430 Q 0,445 90,430 L 85,470 Q 0,485 -85,470 Z" fill="#94a3b8" opacity="0.3" />
                    </g>
                    <g transform="translate(250, 10)">
                      {/* Side Wings */}
                      {[
                        { key: 'front_left_fender', d: "M -100,55 L -160,70 Q -185,80 -185,120 L -185,170 L -110,170 Z", lx: -145, ly: 120 },
                        { key: 'front_left_door', d: "M -115,185 L -200,230 L -210,330 L -120,320 Z", lx: -160, ly: 260 },
                        { key: 'rear_left_door', d: "M -130,340 L -215,360 L -220,480 L -135,480 Z", lx: -175, ly: 410 },
                        { key: 'rear_left_fender', d: "M -135,495 L -225,495 Q -235,560 -210,590 L -120,610 Z", lx: -175, ly: 550 },
                        { key: 'front_right_fender', d: "M 100,55 L 160,70 Q 185,80 185,120 L 185,170 L 110,170 Z", lx: 145, ly: 120 },
                        { key: 'front_right_door', d: "M 115,185 L 200,230 L 210,330 L 120,320 Z", lx: 160, ly: 260 },
                        { key: 'rear_right_door', d: "M 130,340 L 215,360 L 220,480 L 135,480 Z", lx: 175, ly: 410 },
                        { key: 'rear_right_fender', d: "M 135,495 L 225,495 Q 235,560 210,590 L 120,610 Z", lx: 175, ly: 550 },
                      ].map(part => {
                        const status = expertiseReport[part.key];
                        const colorMap = { original: '#e5e7eb', local_painted: '#f97316', painted: '#3b82f6', changed: '#ef4444' };
                        const marker = status === 'changed' ? 'D' : status === 'painted' ? 'B' : status === 'local_painted' ? 'LB' : '';
                        return (
                          <g key={part.key} onClick={() => cycleExpertiseStatus(part.key)}>
                            <path d={part.d} fill={colorMap[status]} stroke="rgba(0,0,0,0.1)" strokeWidth="0.4" />
                            {marker && <text x={part.lx} y={part.ly} fill="white" fontSize="22" fontWeight="1000" textAnchor="middle" style={{ pointerEvents: 'none' }}>{marker}</text>}
                          </g>
                        );
                      })}
                      {/* Silhouette Wheels */}
                      <circle cx="-195" cy="120" r="24" fill="#e5e7eb" stroke="#94a3b8" strokeWidth="0.3" />
                      <circle cx="-210" cy="560" r="24" fill="#e5e7eb" stroke="#94a3b8" strokeWidth="0.3" />
                      <circle cx="195" cy="120" r="24" fill="#e5e7eb" stroke="#94a3b8" strokeWidth="0.3" />
                      <circle cx="210" cy="560" r="24" fill="#e5e7eb" stroke="#94a3b8" strokeWidth="0.3" />
                    </g>
                  </svg>
                </div>
              </div>

              {/* Right Column: Dynamic Status Lists (As in image) */}
              <div style={{ flex: '1', width: '100%', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem' }}>
                {/* Boyalı Parçalar */}
                <div>
                  <h4 style={{ color: '#3b82f6', fontSize: '0.8rem', fontWeight: '900', textTransform: 'uppercase', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid rgba(59,130,246,0.2)', paddingBottom: '0.5rem' }}>
                    <div style={{ width: '8px', height: '8px', background: '#3b82f6' }}></div>
                    Boyalı Parçalar
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {CAR_PARTS.filter(p => expertiseReport[p.key] === 'painted' || expertiseReport[p.key] === 'local_painted').map(p => (
                      <li key={p.key} style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: '#3b82f6' }}>•</span>
                        {p.label} {expertiseReport[p.key] === 'local_painted' ? '(Lokal)' : ''}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Değişen Parçalar */}
                <div>
                  <h4 style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '900', textTransform: 'uppercase', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderBottom: '1px solid rgba(239,68,68,0.2)', paddingBottom: '0.5rem' }}>
                    <div style={{ width: '8px', height: '8px', background: '#ef4444' }}></div>
                    Değişen Parçalar
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {CAR_PARTS.filter(p => expertiseReport[p.key] === 'changed').map(p => (
                      <li key={p.key} style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: '#ef4444' }}>•</span>
                        {p.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>



          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginTop: '1rem' }}>
            <FormNumberInput 
              label="Hasar Kaydı Tutarı (₺)" 
              name="damage_amount" 
              value={formData.damage_amount} 
              onChange={handleInputChange} 
              placeholder="Örn: 15000"
            />
            <small style={{ color: 'rgba(255,255,255,0.3)', marginTop: '0.4rem', display: 'block' }}>
              Hatasız ise 0 bırakabilirsiniz.
            </small>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Ekspertiz Detay Notu</label>
              <textarea 
                name="expertise_note" 
                value={formData.expertise_note} 
                onChange={handleInputChange} 
                style={{ ...fieldStyle, minHeight: '100px', resize: 'vertical' }} 
                placeholder="Araçtaki boyalı/değişen parçaların detayı veya mekanik durum hakkında kısa not..."
              />
            </div>
          </div>

          {/* DONANIM ÖZELLİKLERİ */}
          <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#06b6d4', borderRadius: '4px' }}></div>
              DONANIM ÖZELLİKLERİ
            </h3>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            {Object.entries(FEATURE_GROUPS).map(([groupName, items]) => (
              <div key={groupName} style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#00d1ff', fontSize: '0.9rem', fontWeight: '800', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {groupName}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
                  {items.map(feature => {
                    const isChecked = selectedFeatures.includes(feature);
                    return (
                      <label key={feature} 
                        style={{ 
                          display: 'flex', alignItems: 'center', gap: '0.75rem', 
                          padding: '0.7rem 1rem', borderRadius: '0.8rem', cursor: 'pointer',
                          background: isChecked ? 'rgba(0, 209, 255, 0.08)' : 'rgba(255,255,255,0.02)',
                          border: isChecked ? '1px solid rgba(0, 209, 255, 0.3)' : '1px solid rgba(255,255,255,0.05)',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ 
                          width: '20px', height: '20px', borderRadius: '6px', flexShrink: 0,
                          background: isChecked ? '#00d1ff' : 'rgba(255,255,255,0.05)',
                          border: isChecked ? 'none' : '2px solid rgba(255,255,255,0.15)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}>
                          {isChecked && <span style={{ color: '#000', fontSize: '12px', fontWeight: '900' }}>✓</span>}
                        </div>
                        <input type="checkbox" checked={isChecked} onChange={() => toggleFeature(feature)} style={{ display: 'none' }} />
                        <span style={{ color: isChecked ? '#e2e8f0' : '#94a3b8', fontSize: '0.85rem', fontWeight: isChecked ? '700' : '500' }}>{feature}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Görseller ve Açıklama */}
          <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#10b981', borderRadius: '4px' }}></div>
              GÖRSELLER VE AÇIKLAMA
            </h3>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Araç Resimleri</label>
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {selectedFiles.map((url, idx) => (
                <div 
                  key={idx} 
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(idx)}
                  style={{ 
                    position: 'relative', 
                    width: '140px', 
                    height: '100px', 
                    borderRadius: '1rem', 
                    overflow: 'hidden', 
                    border: '2px solid rgba(255,255,255,0.05)', 
                    boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                    cursor: 'move',
                    transition: 'all 0.3s ease',
                    opacity: draggedItemIndex === idx ? 0.5 : 1,
                    transform: draggedItemIndex === idx ? 'scale(0.95)' : 'scale(1)'
                  }}
                >
                  <img src={url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '4px', background: 'rgba(0,0,0,0.7)', fontSize: '0.6rem', textAlign: 'center', fontWeight: 'bold', pointerEvents: 'none' }}>Görsel {idx + 1}</div>
                  <button 
                    type="button" 
                    onClick={() => handleRemoveImage(idx)}
                    style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: 'white', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: '0 2px 5px rgba(0,0,0,0.5)', zIndex: 10 }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <X size={14} strokeWidth={3} />
                  </button>
                </div>
              ))}
              <label style={{ 
                width: '140px', height: '100px', borderRadius: '1rem', border: '2px dashed rgba(0, 209, 255, 0.4)', 
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                cursor: 'pointer', background: 'rgba(0, 209, 255, 0.05)', transition: 'all 0.3s' 
                }}
                onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(0, 209, 255, 0.15)'; e.currentTarget.style.borderColor = '#00d1ff'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(0, 209, 255, 0.05)'; e.currentTarget.style.borderColor = 'rgba(0, 209, 255, 0.4)'; }}
              >
                <UploadCloud size={28} color="#00d1ff" />
                <span style={{ fontSize: '0.75rem', color: 'white', marginTop: '8px', fontWeight: '800' }}>GÖRSEL YÜKLE</span>
                <input type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} accept="image/*" />
              </label>
            </div>
            <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>İdeal görsel boyutu 1920x1080 (Yatay). <b>Resimleri sürükleyerek sıralarını değiştirebilirsiniz.</b></p>
          </div>


          {/* İLAN SAHİBİ BİLGİLERİ */}
          <div style={{ gridColumn: '1 / -1', marginTop: '1.5rem' }}>
            <h3 style={hrStyle}>
              <div style={{ width: '8px', height: '24px', background: '#f59e0b', borderRadius: '4px' }}></div>
              İLAN SAHİBİ / DANIŞMAN BİLGİLERİ
            </h3>
          </div>

          <div style={{ gridColumn: '1 / 2' }}>
            <label style={labelStyle}>Danışman Seç (Opsiyonel)</label>
            <FormSelect 
              label=""
              name="selected_consultant_id"
              value={formData.selected_consultant_id}
              onChange={handleConsultantChange}
              options={[
                { value: '', label: 'Seçiniz...' },
                { value: 'manual', label: 'Manuel Giriş Yap' },
                ...adminUsers.map(u => ({ value: u.id, label: u.full_name || u.username }))
              ]}
              hideSpacer={true}
            />
          </div>

          <div style={{ gridColumn: '2 / 4' }}>
            <label style={labelStyle}>İlan Sahibi İsim Soyisim</label>
            <input 
              type="text" 
              name="seller_name" 
              value={formData.seller_name} 
              onChange={handleInputChange} 
              placeholder="Örn: Oğuzhan Eren" 
              style={fieldStyle} 
            />
          </div>

          <div style={{ gridColumn: '4 / 5' }}>
            <label style={labelStyle}>İlan Sahibi Telefon</label>
            <input 
              type="text" 
              name="seller_phone" 
              value={formData.seller_phone} 
              onChange={handleInputChange} 
              placeholder="Örn: +90 532 158 56 46" 
              style={fieldStyle} 
            />
          </div>

          {/* İÇ ENVANTER VE FİNANSAL BİLGİLER (Sadece Admin Görür) */}
          <div style={{ gridColumn: '1 / -1', marginTop: '2.5rem' }}>
            <h3 style={{...hrStyle, borderBottom: '2px solid rgba(16, 185, 129, 0.2)'}}>
              <div style={{ width: '8px', height: '24px', background: '#10b981', borderRadius: '4px' }}></div>
              İÇ ENVANTER VE MALİYET BİLGİLERİ
              <span style={{ fontSize: '0.7rem', opacity: 0.5, marginLeft: 'auto', fontWeight: '400' }}>
                * BU BİLGİLER TİCARİ SIR OLARAK SAKLANIR VE WEB SİTESİNDE GÖRÜNMEZ
              </span>
            </h3>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>ALIŞ FİYATI (MALİYET ₺)</label>
            <FormNumberInput 
              name="purchase_cost" 
              value={formData.purchase_cost} 
              onChange={handleInputChange} 
              placeholder="Örn: 1.100.000"
              hideSpacer={true}
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>EKSTRA MASRAFLAR (DETAYLI)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', background: 'rgba(255,255,255,0.02)', padding: '1.5rem', borderRadius: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
              {expenseItems.map((item, index) => (
                <div key={index} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                  <div style={{ flex: 2 }}>
                    <input 
                      type="text" 
                      value={item.description} 
                      onChange={(e) => {
                        const newItems = [...expenseItems];
                        newItems[index].description = e.target.value;
                        setExpenseItems(newItems);
                      }}
                      placeholder="Parça / Masraf Adı (Örn: Pasta Cila)"
                      style={{...fieldStyle, padding: '0.8rem 1.2rem'}} 
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input 
                      type="text" 
                      inputMode="numeric"
                      value={item.amount !== '' && item.amount !== null ? Number(item.amount).toLocaleString('tr-TR') : ''}
                      onChange={(e) => {
                        const rawValue = e.target.value.replace(/[^0-9]/g, '');
                        const newItems = [...expenseItems];
                        newItems[index].amount = rawValue;
                        setExpenseItems(newItems);
                        
                        // Update total in real-time
                        const total = newItems.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
                        setFormData(prev => ({ ...prev, extra_expenses: total }));
                      }}
                      placeholder="Tutar (₺)"
                      style={{...fieldStyle, padding: '0.8rem 1.2rem'}} 
                    />
                  </div>
                  <button 
                    type="button" 
                    onClick={() => {
                      if (expenseItems.length > 1) {
                        const newItems = expenseItems.filter((_, i) => i !== index);
                        setExpenseItems(newItems);
                        const total = newItems.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
                        setFormData(prev => ({ ...prev, extra_expenses: total }));
                      } else {
                        setExpenseItems([{ description: '', amount: '' }]);
                        setFormData(prev => ({ ...prev, extra_expenses: '' }));
                      }
                    }}
                    style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', width: '38px', height: '38px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.3s' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              
              <button 
                type="button" 
                onClick={() => setExpenseItems([...expenseItems, { description: '', amount: '' }])}
                style={{ 
                  marginTop: '0.5rem', padding: '0.8rem', borderRadius: '12px', background: 'rgba(0, 209, 255, 0.1)', 
                  color: '#00d1ff', border: '1px dashed rgba(0, 209, 255, 0.3)', fontWeight: '800', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.85rem'
                }}
              >
                <Plus size={16} /> YENİ MASRAF KALEMİ EKLE
              </button>

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#94a3b8' }}>TOPLAM EKSTRA MASRAF:</span>
                <span style={{ fontSize: '1.1rem', fontWeight: '900', color: '#00d1ff' }}>
                  ₺ {formData.extra_expenses ? Number(formData.extra_expenses).toLocaleString('tr-TR') : '0'}
                </span>
              </div>
            </div>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>ALINDIĞI KİŞİ / FİRMA</label>
            <input 
              type="text" 
              name="purchased_from" 
              value={formData.purchased_from} 
              onChange={handleInputChange} 
              style={fieldStyle} 
              placeholder="Örn: ABC Galeri veya Ahmet Yılmaz" 
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>NOTER SATIŞ TARİHİ</label>
            <input 
              type="date" 
              name="purchase_date" 
              value={formData.purchase_date ? formData.purchase_date.split('T')[0] : ''} 
              onChange={handleInputChange} 
              style={{...fieldStyle, colorScheme: 'dark'}} 
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>ANLIK OPERASYONEL DURUM</label>
            <textarea 
              name="operational_status" 
              value={formData.operational_status} 
              onChange={handleInputChange} 
              style={{ ...fieldStyle, minHeight: '80px', resize: 'vertical' }} 
              placeholder="Örn: Sanayide bakımda / Kuaförde / Kapora alındı..."
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>DİĞER İÇ NOTLAR</label>
            <textarea 
              name="internal_notes" 
              value={formData.internal_notes} 
              onChange={handleInputChange} 
              style={{ ...fieldStyle, minHeight: '80px', resize: 'vertical' }} 
              placeholder="Sadece sizin göreceğiniz özel notlar..."
            />
          </div>

          {/* ADMİN ÖZEL VERİLERİ (GİZLİ) */}
          <div style={{ gridColumn: '1 / -1', marginTop: '2.5rem' }}>
            <h3 style={{...hrStyle, borderBottom: '2px solid rgba(255, 107, 107, 0.2)'}}>
              <div style={{ width: '8px', height: '24px', background: '#ff6b6b', borderRadius: '4px' }}></div>
              ADMİN ÖZEL VERİLERİ (PLAKA VE ŞASİ TAKİBİ)
              <span style={{ fontSize: '0.7rem', opacity: 0.5, marginLeft: 'auto', fontWeight: '400' }}>
                * BU ALANLAR WEB ARAYÜZÜNDE GÖSTERİLMEZ
              </span>
            </h3>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <LicensePlateInput 
              name="license_plate" 
              value={formData.license_plate} 
              onChange={handleInputChange} 
            />
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>ŞASİ NUMARASI (VIN)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <input 
                  type="text" 
                  name="chassis_no" 
                  value={formData.chassis_no} 
                  onChange={handleInputChange} 
                  style={{...fieldStyle, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold'}} 
                  placeholder="Örn: WBA1234567890ABC" 
                />
              </div>
              <div style={{ width: '54px', minWidth: '54px' }}></div>
            </div>
          </div>



          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>İlan Açıklaması</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows="8" style={{...fieldStyle, resize: 'vertical'}} placeholder="Araç ile ilgili öne çıkan tüm özellikler, bakım geçmişi ve ekspertiz detaylarını buraya yazabilirsiniz..." />
          </div>

          {/* PLANLAMA VE TAKVİM GEÇMİŞİ (Sadece Düzenleme Modunda) */}
          {id && (
            <div style={{ gridColumn: '1 / -1', marginTop: '3rem' }}>
              <div style={{...hrStyle, borderBottom: '2px solid rgba(0, 209, 255, 0.3)', marginBottom: '2rem'}}>
                <div style={{ width: '10px', height: '30px', background: 'linear-gradient(to bottom, #00d1ff, #0072ff)', borderRadius: '4px' }}></div>
                ARAÇ MÜSAİTLİK DURUMU VE TAKVİM GEÇMİŞİ
                <Clock size={20} style={{ marginLeft: 'auto', opacity: 0.7 }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 350px', gap: '2rem' }}>
                {/* CALENDAR VIEW */}
                <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '2rem', border: '1px solid rgba(255,255,255,0.05)', padding: '2rem', height: 'fit-content' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                      <Calendar size={20} color="var(--primary)" />
                      <h4 style={{ fontWeight: '800', textTransform: 'uppercase', fontSize: '0.9rem', letterSpacing: '1px' }}>MÜSAİTLİK TAKVİMİ</h4>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(255,255,255,0.03)', padding: '0.4rem 0.8rem', borderRadius: '10px' }}>
                        <button type="button" onClick={handlePrevMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><ChevronLeft size={16} /></button>
                        <span style={{ fontSize: '0.85rem', fontWeight: '800', minWidth: '100px', textAlign: 'center' }}>
                          {currentDate.toLocaleString('tr-TR', { month: 'long', year: 'numeric' })}
                        </span>
                        <button type="button" onClick={handleNextMonth} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}><ChevronRight size={16} /></button>
                      </div>
                      <button 
                        type="button" 
                        onClick={() => { setEditingId(null); setIsModalOpen(true); }}
                        style={{ background: 'var(--primary)', color: 'black', border: 'none', width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      >
                        <Plus size={20} />
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderRadius: '1rem', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                    {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(d => (
                      <div key={d} style={{ padding: '0.5rem', background: 'rgba(255,255,255,0.03)', textAlign: 'center', fontSize: '0.65rem', fontWeight: '900', color: '#64748b', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>{d}</div>
                    ))}
                    {renderCalendar()}
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.65rem', fontWeight: '700', color: '#64748b' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#22c55e' }}></div> KİRALAMA
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.65rem', fontWeight: '700', color: '#64748b' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#f59e0b' }}></div> REZERVE
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.65rem', fontWeight: '700', color: '#64748b' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#ef4444' }}></div> BAKIM / SERVİS
                    </div>
                  </div>
                </div>

                {/* SIDE HISTORY LIST */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '600px', overflowY: 'auto', paddingRight: '0.5rem' }}>
                  {availabilityHistory.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b', background: 'rgba(255,255,255,0.01)', borderRadius: '2rem', border: '1px dashed rgba(255,255,255,0.1)' }}>
                      <Info size={32} style={{ marginBottom: '1rem', opacity: 0.3 }} />
                      <p style={{ fontSize: '0.8rem' }}>Henüz kayıt yok.</p>
                    </div>
                  ) : (
                    availabilityHistory.sort((a, b) => new Date(b.start_date) - new Date(a.start_date)).map((item, idx) => {
                      const isCurrent = new Date(item.start_date) <= new Date() && new Date(item.end_date) >= new Date();
                      const statusColor = (item.type === 'Bakım' || item.type === 'Serviste' || item.type === 'Temizlikte') ? '#ef4444' : 
                                         (item.type === 'Rezerve' || item.type === 'Kapora Alındı') ? '#f59e0b' : 
                                         '#22c55e';
                      
                      return (
                        <div key={idx} style={{ 
                          background: isCurrent ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)', 
                          padding: '1.25rem', borderRadius: '1.5rem', border: '1px solid rgba(255,255,255,0.05)',
                          position: 'relative'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{ width: '6px', height: '6px', borderRadius: 'full', background: statusColor, boxShadow: `0 0 8px ${statusColor}` }}></div>
                              <span style={{ fontWeight: '900', textTransform: 'uppercase', fontSize: '0.8rem', color: 'white' }}>{item.type}</span>
                            </div>
                            <button type="button" onClick={() => handleDeleteReservation(item.id)} style={{ background: 'none', border: 'none', color: '#ef4444', opacity: 0.5, cursor: 'pointer' }}><X size={14} /></button>
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                            {new Date(item.start_date).toLocaleDateString('tr-TR')} — {new Date(item.end_date).toLocaleDateString('tr-TR')}
                          </div>
                          {item.notes && <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>"{item.notes}"</div>}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '1.5rem', marginTop: '3rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '3rem' }}>
            <button type="submit" disabled={loading} style={{ 
              flex: '1', padding: '1.2rem', borderRadius: '1.25rem', background: 'linear-gradient(135deg, #00d1ff 0%, #0072ff 100%)', 
              color: 'white', border: 'none', fontWeight: '800', cursor: 'pointer', display: 'flex', 
              alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '1.1rem',
              boxShadow: '0 10px 30px rgba(0, 209, 255, 0.3)', transition: 'all 0.3s'
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(0, 209, 255, 0.4)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 209, 255, 0.3)'; }}
            >
              <Save size={22} /> {id ? 'DEĞİŞİKLİKLERİ KAYDET' : 'ARACI SİSTEME EKLE'}
            </button>
            <button type="button" onClick={() => navigate('/admin/vehicles')} style={{ 
              flex: '1', padding: '1.2rem', borderRadius: '1.25rem', background: 'rgba(255, 255, 255, 0.05)', 
              color: 'white', border: '1px solid rgba(255,255,255,0.1)', fontWeight: '700', cursor: 'pointer',
              transition: 'all 0.3s', fontSize: '1.1rem'
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'; }}
            >
              İPTAL
            </button>
          </div>
        </form>
      </div>

      {/* Modals outside the form to avoid nested submission issues */}
      {isDayModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 11000, padding: '1rem' }}>
          <div style={{ background: '#0f172a', width: '100%', maxWidth: '500px', borderRadius: '2rem', border: '1px solid rgba(255,255,255,0.1)', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h3 style={{ color: 'white', fontSize: '1.2rem', fontWeight: '900' }}>{activeDayLabel} — Gün Özeti</h3>
              <button type="button" onClick={() => setIsDayModalOpen(false)} style={{ color: 'white', background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '50vh', overflowY: 'auto' }}>
              {selectedDayEvents.map(ev => (
                <div key={ev.id} style={{ background: 'rgba(255,255,255,0.03)', padding: '1.25rem', borderRadius: '1.25rem', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontSize: '0.7rem', fontWeight: '900', padding: '2px 8px', borderRadius: '6px', background: (ev.type === 'Bakım' || ev.type === 'Serviste') ? '#ef4444' : '#22c55e', color: 'black', textTransform: 'uppercase' }}>{ev.type}</span>
                    <div style={{ color: 'white', fontWeight: '800', marginTop: '0.5rem' }}>{new Date(ev.start_date).toLocaleTimeString('tr-TR', {hour:'2-digit', minute:'2-digit'})} - {new Date(ev.end_date).toLocaleTimeString('tr-TR', {hour:'2-digit', minute:'2-digit'})}</div>
                    {ev.notes && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.2rem' }}>{ev.notes}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" onClick={() => { setEditingId(ev.id); setNewReservation({ start_date: ev.start_date.slice(0, 16), end_date: ev.end_date.slice(0, 16), type: ev.type, notes: ev.notes || '' }); setIsModalOpen(true); setIsDayModalOpen(false); }} style={{ background: 'var(--primary)', border: 'none', padding: '0.5rem 1rem', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '0.75rem' }}>DÜZENLE</button>
                    <button type="button" onClick={() => { handleDeleteReservation(ev.id); setIsDayModalOpen(false); }} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', padding: '0.5rem', borderRadius: '10px', cursor: 'pointer' }}><Trash2 size={18} /></button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => { setEditingId(null); setNewReservation({ start_date: `${selectedDateStr}T09:00`, end_date: `${selectedDateStr}T18:00`, type: 'Kiralama', notes: '' }); setIsModalOpen(true); setIsDayModalOpen(false); }} style={{ width: '100%', marginTop: '2rem', padding: '1.25rem', borderRadius: '1.25rem', background: 'var(--primary)', color: 'black', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>+ YENİ KAYIT EKLE</button>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 12000, padding: '1rem' }}>
          <div style={{ background: '#0f172a', width: '100%', maxWidth: '540px', borderRadius: '2rem', border: '1px solid rgba(255,255,255,0.1)', padding: '2.5rem', position: 'relative' }}>
            <button 
              type="button" 
              onClick={() => setIsModalOpen(false)} 
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'rgba(255,255,255,0.05)', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' }}
              onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            >
              <X size={20} />
            </button>

            <h3 style={{ color: 'white', fontSize: '1.3rem', fontWeight: '800', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Calendar size={24} color="var(--primary)" />
              {editingId ? 'Kaydı Düzenle' : 'Yeni Planlama Ekle'}
            </h3>
            
            <form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleAddReservation(e); }} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                <div style={{ position: 'relative' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block' }}>BAŞLANGIÇ</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type="datetime-local" 
                      required 
                      value={newReservation.start_date} 
                      onChange={(e) => setNewReservation({...newReservation, start_date: e.target.value})} 
                      style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem 2.5rem 0.8rem 0.5rem', color: 'white', colorScheme: 'dark', outline: 'none', fontSize: '0.85rem' }} 
                    />
                    <button 
                      type="button"
                      onClick={(e) => { e.currentTarget.previousSibling.blur(); }}
                      style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'rgba(239, 68, 68, 0.2)', border: 'none', color: '#ef4444', width: '24px', height: '24px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}
                      title="Takvimi Kapat"
                    >
                      <X size={14} strokeWidth={3} />
                    </button>
                  </div>
                  <small style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.4rem', display: 'block' }}>Takvimi kapatmak için kırmızı X'e veya boşluğa basın.</small>
                </div>
                <div style={{ position: 'relative' }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block' }}>BİTİŞ</label>
                  <div style={{ position: 'relative' }}>
                    <input 
                      type="datetime-local" 
                      required 
                      value={newReservation.end_date} 
                      onChange={(e) => setNewReservation({...newReservation, end_date: e.target.value})} 
                      style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem 2.5rem 0.8rem 0.5rem', color: 'white', colorScheme: 'dark', outline: 'none', fontSize: '0.85rem' }} 
                    />
                    <button 
                      type="button"
                      onClick={(e) => { e.currentTarget.previousSibling.blur(); }}
                      style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'rgba(239, 68, 68, 0.2)', border: 'none', color: '#ef4444', width: '24px', height: '24px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5 }}
                      title="Takvimi Kapat"
                    >
                      <X size={14} strokeWidth={3} />
                    </button>
                  </div>
                  <small style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '0.4rem', display: 'block' }}>Takvimi kapatmak için kırmızı X'e veya boşluğa basın.</small>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block' }}>DURUM TÜRÜ</label>
                <select value={newReservation.type} onChange={(e) => setNewReservation({...newReservation, type: e.target.value})} style={{ width: '100%', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '0.8rem', color: 'white', outline: 'none' }}>
                  {typeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', marginBottom: '0.5rem', display: 'block' }}>ÖZEL NOTLAR</label>
                <textarea value={newReservation.notes} onChange={(e) => setNewReservation({...newReservation, notes: e.target.value})} style={{ width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem', color: 'white', minHeight: '100px', outline: 'none', resize: 'vertical' }} placeholder="Müşteri planı, bakım içeriği vb." />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ flex: 1, padding: '1.2rem', borderRadius: '1.25rem', background: 'rgba(255,255,255,0.05)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}>İPTAL</button>
                <button type="submit" style={{ flex: 2, padding: '1.2rem', borderRadius: '1.25rem', background: 'var(--primary)', color: 'black', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>{editingId ? 'GÜNCELLE' : 'KAYDET'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddVehicle;
